import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, impersonationAPI } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  codice_fiscale: string;
  nome: string;
  cognome: string;
  ore_lavoro: number;
  giorni_lavoro: number;
  role: string;
  department: string;
  must_change_password?: boolean;
  is_super_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
  isImpersonating: boolean;
  checkImpersonation: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Funzione per verificare lo stato dell'impersonazione
  const checkImpersonation = async () => {
    // Controlla se siamo in modalità "vedi admin" (non consideriamo impersonazione attiva)
    const viewAdminMode = localStorage.getItem('view_admin_mode') === 'true';
    if (viewAdminMode) {
      // In modalità "vedi admin", non siamo in impersonazione (mostriamo area admin)
      setIsImpersonating(false);
      return false;
    }
    
    // Controlla se c'è impersonazione attiva (diretta o in backup)
    const impersonatedUserId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
    if (!impersonatedUserId) {
      setIsImpersonating(false);
      return false;
    }
    
    // Se user non è ancora caricato, aspetta
    if (!user) {
      setIsImpersonating(false);
      return false;
    }
    
    try {
      // Se c'è solo backup ma non impersonated_user_id, ripristinalo
      const currentId = localStorage.getItem('impersonated_user_id');
      const backupId = localStorage.getItem('impersonated_user_id_backup');
      if (!currentId && backupId) {
        localStorage.setItem('impersonated_user_id', backupId);
      }
      
      const context = await impersonationAPI.getImpersonationContext();
      if (context.active && context.impersonated_user) {
        setIsImpersonating(true);
        // Aggiorna anche user con i dati dell'utente impersonato
        setUser(context.impersonated_user);
        return true;
      } else {
        // Se l'API restituisce active: false, verifica se c'è ancora impersonated_user_id in localStorage
        // Se c'è, potrebbe essere che l'API non ha trovato la sessione ma l'impersonazione è ancora attiva
        // (ad esempio, quando si impersona un utente normale)
        const stillHasId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
        if (stillHasId) {
          // C'è ancora impersonated_user_id, quindi l'impersonazione potrebbe essere ancora attiva
          // Non rimuovere localStorage, lascia che il banner gestisca il fallback
          setIsImpersonating(true);
          return true;
        }
        // Solo se non c'è più impersonated_user_id, pulisci localStorage
        setIsImpersonating(false);
        localStorage.removeItem('impersonated_user_id');
        localStorage.removeItem('impersonated_user_id_backup');
        localStorage.removeItem('view_admin_mode');
        return false;
      }
    } catch (err: any) {
      // Se l'errore è 403 o 500, potrebbe significare che:
      // 1. L'utente corrente non è admin (ma questo è normale durante impersonazione)
      // 2. La sessione è scaduta o non valida
      // In ogni caso, se c'è impersonated_user_id in localStorage, consideriamo impersonazione attiva
      // e usiamo i dati dell'utente corrente (che è quello impersonato)
      if (err.response?.status === 403 || err.response?.status === 500) {
        // Se c'è ancora impersonated_user_id in localStorage, consideriamo impersonazione attiva
        // Questo gestisce il caso in cui si impersona un utente normale
        const stillHasId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
        if (stillHasId && user) {
          // L'utente corrente è quello impersonato, quindi impersonazione è attiva
          setIsImpersonating(true);
          return true;
        }
        // Altrimenti, pulisci tutto
        localStorage.removeItem('impersonated_user_id');
        localStorage.removeItem('impersonated_user_id_backup');
        localStorage.removeItem('view_admin_mode');
        setIsImpersonating(false);
        return false;
      }
      setIsImpersonating(false);
      return false;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          // Controlla se siamo in modalità "vedi admin"
          const viewAdminMode = localStorage.getItem('view_admin_mode') === 'true';
          
          // Se siamo in modalità "vedi admin", carica il profilo dell'utente originale (admin)
          // NON controllare l'impersonazione perché vogliamo vedere l'area admin
          if (viewAdminMode) {
            setIsImpersonating(false);
            const response = await authAPI.getProfile(storedToken);
            setToken(storedToken);
            setUser(response.user);
            setLoading(false);
            return;
          }
          
          // Verifica se c'è impersonazione attiva
          const impersonating = await checkImpersonation();
          
          // Se c'è impersonazione attiva, user è già stato impostato da checkImpersonation
          if (impersonating) {
            setToken(storedToken);
            setLoading(false);
            return;
          }

          // Verifica il token e recupera i dati dell'utente
          // Se c'è impersonazione attiva, GetProfile dovrebbe restituire i dati dell'utente impersonato
          // (grazie al middleware di impersonazione che imposta user nel contesto)
          const response = await authAPI.getProfile(storedToken);
          
          // Se c'è ancora impersonazione attiva dopo GetProfile, verifica che user contenga i dati corretti
          const stillImpersonating = localStorage.getItem('impersonated_user_id');
          if (stillImpersonating && response.user?.role === 'admin') {
            // Se c'è impersonazione attiva ma GetProfile restituisce ancora admin,
            // significa che il middleware non ha funzionato correttamente
            // Ricarica i dati dell'utente impersonato direttamente
            try {
              const impersonationContext = await impersonationAPI.getImpersonationContext();
              if (impersonationContext.active && impersonationContext.impersonated_user) {
                setToken(storedToken);
                setUser(impersonationContext.impersonated_user);
                setLoading(false);
                return;
              }
            } catch (err) {
              // Errore recupero dati impersonato
            }
          }
          
          setToken(storedToken);
          setUser(response.user);
        } catch (error) {
          // Token non valido, rimuovilo
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      const { token: newToken, user: userData } = response;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        // Controlla se siamo in modalità "vedi admin"
        const viewAdminMode = localStorage.getItem('view_admin_mode') === 'true';
        
        // Se siamo in modalità "vedi admin", carica il profilo dell'utente originale (admin)
        if (viewAdminMode) {
          const response = await authAPI.getProfile(storedToken);
          setUser(response.user);
          return;
        }
        
        // Verifica se c'è impersonazione attiva
        // IMPORTANTE: Se non c'è impersonated_user_id, significa che l'impersonazione è stata terminata
        // e dobbiamo caricare il profilo dell'utente originale (admin)
        const impersonatedUserId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
        
        // Se NON c'è impersonated_user_id, carica direttamente il profilo normale (admin)
        if (!impersonatedUserId) {
          const response = await authAPI.getProfile(storedToken);
          setUser(response.user);
          setIsImpersonating(false);
          return;
        }
        
        // Se c'è impersonated_user_id, verifica lo stato della sessione
        if (impersonatedUserId) {
          try {
            const impersonationContext = await impersonationAPI.getImpersonationContext();
            if (impersonationContext.active && impersonationContext.impersonated_user) {
              // Usa i dati dell'utente impersonato
              setUser(impersonationContext.impersonated_user);
              return;
            } else {
              // Se l'API restituisce active: false, verifica se c'è ancora impersonated_user_id in localStorage
              // Se c'è, potrebbe essere che l'API non ha trovato la sessione ma l'impersonazione è ancora attiva
              const stillHasId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
              if (stillHasId) {
                // C'è ancora impersonated_user_id, quindi l'impersonazione potrebbe essere ancora attiva
                // Non rimuovere localStorage, lascia che il banner gestisca il fallback
                // Carica il profilo normale (che sarà quello dell'utente impersonato dopo il reload)
                const response = await authAPI.getProfile(storedToken);
                setUser(response.user);
                return;
              }
              // Solo se non c'è più impersonated_user_id, pulisci localStorage
              localStorage.removeItem('impersonated_user_id');
              localStorage.removeItem('impersonated_user_id_backup');
              localStorage.removeItem('view_admin_mode');
            }
          } catch (err: any) {
            // Se l'errore è 403 o 500, potrebbe significare che si sta impersonando un utente normale
            // In questo caso, non rimuovere localStorage se c'è ancora impersonated_user_id
            const stillHasId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
            if (stillHasId && (err.response?.status === 403 || err.response?.status === 500)) {
              // Carica il profilo normale (che sarà quello dell'utente impersonato dopo il reload)
              const response = await authAPI.getProfile(storedToken);
              setUser(response.user);
              return;
            }
            // Per altri errori o se non c'è più impersonated_user_id, rimuovi da localStorage
            localStorage.removeItem('impersonated_user_id');
            localStorage.removeItem('impersonated_user_id_backup');
            localStorage.removeItem('view_admin_mode');
          }
        }
        
        const response = await authAPI.getProfile(storedToken);
        setUser(response.user);
      } catch (error) {
        // Errore refresh utente
      }
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    refreshUser,
    loading,
    isImpersonating,
    checkImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
